import * as React from 'react';
import cn from 'classnames';

import { InputProps, InputSize } from 'shared/newEntities/components/Input';
import { CloseIcon } from 'shared/newComponents/icons';
import { IconSize } from 'shared/entities/components/Icon';
import { ComponentType } from 'shared/entities/components/Component';
import {
  renderTranslationString,
  useTypedTranslation
} from 'shared/entities/localization';
import { useModalState } from 'shared/entities/common/hooks';
import Typography from 'shared/newComponents/Typography';
import {
  TypographyColor,
  TypographySize,
  TypographyType
} from 'shared/newEntities/components/Typography';
import CopyComponent from 'shared/newComponents/CopyComponent';
import CroppedContent from 'shared/newComponents/CroppedContent';

import WithMeta from '../WithMeta';

import TrailingExtras from './TrailingExtras';
import styles from './Input.modules.scss';

const Input = React.forwardRef(
  (
    {
      onChange,
      onBlur,
      onFocus,
      value,
      placeholder,
      error,
      label,
      className,
      bottomLabel,
      size = InputSize.sm,
      disabled,
      type = 'text',
      reference,
      withCopying,
      Icon,
      prefix,
      prefixFontColor = TypographyColor.tertiary,
      withoutPrefixBorder = false,
      inputStyle,
      errorLimited,
      fontSize = 'm',
      currency,
      withoutBorder,
      forceFocus = false,
      right,
      onClear,
      showErrorText,
      endOfLine,
      labelReference,
      withTimeoutOnBlur = true,
      errorSize,
      withoutErrorIcon = false,
      ...rest
    }: InputProps,
    ref: React.MutableRefObject<HTMLInputElement>
  ) => {
    const {
      opened: focused,
      open: focus,
      close: unfocus
    } = useModalState(forceFocus);
    const [innerType, setInnerType] =
      React.useState<React.HTMLInputTypeAttribute>(type);
    const { t } = useTypedTranslation();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const actualRef = ref ?? inputRef;

    const handleChangePasswordType = React.useCallback(() => {
      if (type !== 'password') {
        return;
      }
      setInnerType(innerType === 'password' ? 'text' : 'password');
    }, [type, innerType]);

    const handleChange = React.useCallback<
      React.ChangeEventHandler<HTMLInputElement>
    >(
      (e) => {
        onChange?.(e.target.value, e);
      },
      [onChange]
    );

    const handleBlur = React.useCallback<
      React.FocusEventHandler<HTMLInputElement>
    >(
      (e) => {
        if (withTimeoutOnBlur) {
          // для того, чтобы handleChangePasswordType вызывался до валидации
          setTimeout(() => onBlur?.(e), 200);
        } else {
          onBlur?.(e);
        }

        unfocus();
      },
      [onBlur]
    );

    const handleFocus = React.useCallback<
      React.FocusEventHandler<HTMLInputElement>
    >(
      (e) => {
        onFocus?.(e);
        focus();
      },
      [onFocus]
    );

    React.useEffect(() => {
      if (forceFocus && actualRef.current && focused) {
        actualRef.current.focus();
      }
    }, [forceFocus, focused]);

    const isError = !!error;
    const showErrorIconInField = isError && !withoutErrorIcon;
    const isPasswordField = type === 'password';
    const actualPlaceholder = placeholder
      ? placeholder
      : (t) => t('form.Input.placeholder', { ns: 'newComponents' });

    const content = (
      <div
        styleName={cn(
          'container',
          isError && !withoutBorder && 'container_error',
          focused && !withoutBorder && 'container_focused',
          disabled && !withoutBorder && 'container_disabled',
          (withCopying || !!endOfLine) && 'container_copying',
          (withoutBorder || endOfLine) && 'container_without-border'
        )}
        className={styles[`container_${size}`]}
      >
        <div
          styleName={cn(
            'left',
            withoutBorder && 'left_without-border',
            endOfLine && 'left_with-end'
          )}
        >
          {prefix && (
            <Typography
              fontType={TypographyType.text}
              fontSize={TypographySize[fontSize]}
              fontColor={prefixFontColor}
              styleName={cn(
                'left__prefix',
                withoutPrefixBorder && 'left__prefix_without-border'
              )}
            >
              {prefix}
            </Typography>
          )}
          <div styleName="left__container" style={inputStyle}>
            {Icon && <Icon styleName="left-icon" iconSize={IconSize.XM} />}
            <input
              ref={actualRef}
              {...rest}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={renderTranslationString(actualPlaceholder, t)}
              value={value}
              onFocus={handleFocus}
              styleName="input"
              className={styles[`input_font-size-${fontSize}`]}
              type={innerType}
            />
            {onClear && value && (
              <CloseIcon
                iconSize={IconSize.S}
                styleName="clear-icon"
                action={{
                  type: ComponentType.svg,
                  onClick: onClear
                }}
              />
            )}
            {right && <div styleName="right">{right}</div>}
            <TrailingExtras
              isPasswordField={isPasswordField}
              innerType={innerType}
              showErrorIconInField={showErrorIconInField}
              isError={isError}
              error={error}
              currency={currency}
              reference={reference}
              onTogglePasswordVisibility={handleChangePasswordType}
            />
          </div>
        </div>
        {withCopying && (
          <CopyComponent
            value={value}
            styleName={cn('copying', withoutBorder && 'copying_without-border')}
          />
        )}
        {endOfLine && (
          <div styleName="end-of-line">
            <div
              styleName={cn(
                'end-of-line__left',
                focused && 'end-of-line__left_hidden'
              )}
            />
            <div styleName="end-of-line__content-wrapper">
              <CroppedContent>
                <Typography
                  styleName="end-of-line__content"
                  fontType={TypographyType.text}
                  fontSize={TypographySize.s}
                  fontColor={TypographyColor.main}
                >
                  {endOfLine}
                </Typography>
              </CroppedContent>
            </div>
          </div>
        )}
      </div>
    );

    return (
      <WithMeta
        label={label}
        error={error}
        className={className}
        bottomLabel={bottomLabel}
        errorLimited={errorLimited}
        showErrorText={showErrorText}
        reference={labelReference}
        errorSize={errorSize}
      >
        {content}
      </WithMeta>
    );
  }
);

export default Input;
